import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { environment } from '../../environments/environment';

export interface StockMovement {
  movement_id: number;
  pipe_id: number;
  transaction_type: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  reference_number: string;
  transaction_date: string;
  from_area_code: number;
  to_area_code: number;
  reason: string;
  performed_by: string;
  created_at: string;
}

export interface PipeData {
  pipe_id: number;
  pipe_tubo_type: string;
  qty_on_stock: number;
  area_code: number;
  area_name: string;
}

export interface StockMovementDialogData {
  pipe?: PipeData;
  isNew?: boolean;
}

@Component({
  selector: 'app-stock-movement-dialog',
  templateUrl: './stock-movement-dialog.component.html',
  styleUrls: ['./stock-movement-dialog.component.scss']
})
export class StockMovementDialogComponent implements OnInit {
  movementForm: FormGroup;
  areas: any[] = [];
  movements: StockMovement[] = [];
  dataSource = new MatTableDataSource<StockMovement>();
  displayedColumns: string[] = [
    'transaction_date', 'transaction_type', 'quantity', 'reference_number',
    'from_area', 'to_area', 'reason', 'performed_by'
  ];
  loading = false;
  showAddForm = false;
  isNewMovement = false;
  pipeData: PipeData;

  constructor(
    private dialogRef: MatDialogRef<StockMovementDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StockMovementDialogData,
    private fb: FormBuilder,
    private http: HttpClient
  ) {
    this.pipeData = data.pipe;
    this.isNewMovement = data.isNew || false;
    this.initForm();
  }

  ngOnInit(): void {
    this.loadAreas();
    if (!this.isNewMovement) {
      this.loadStockMovements();
    }
  }

  initForm(): void {
    this.movementForm = this.fb.group({
      transaction_type: ['purchase', Validators.required],
      quantity: [1, [Validators.required, Validators.min(1)]],
      unit_price: [0, [Validators.required, Validators.min(0)]],
      reference_number: [''],
      transaction_date: [new Date().toISOString().split('T')[0], Validators.required],
      from_area_code: [this.pipeData?.area_code || ''],
      to_area_code: [''],
      reason: ['', Validators.required],
      performed_by: ['']
    });

    // Watch for transaction type changes to show/hide relevant fields
    this.movementForm.get('transaction_type')?.valueChanges.subscribe(value => {
      this.onTransactionTypeChange(value);
    });
  }

  loadAreas(): void {
    this.http.get(`${environment.apiServiceBaseUrl}/hawdco/areas`).subscribe({
      next: (data: any) => {
        this.areas = data;
      },
      error: (error) => {
        console.error('Error loading areas:', error);
      }
    });
  }

  loadStockMovements(): void {
    this.loading = true;
    this.http.get<StockMovement[]>(`${environment.apiServiceBaseUrl}/hawdco/pipe-inventory/${this.pipeData.pipe_id}/stock-movements`).subscribe({
      next: (data) => {
        this.movements = data;
        this.dataSource.data = data;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading stock movements:', error);
        this.loading = false;
      }
    });
  }

  onTransactionTypeChange(transactionType: string): void {
    const fromAreaControl = this.movementForm.get('from_area_code');
    const toAreaControl = this.movementForm.get('to_area_code');
    const reasonControl = this.movementForm.get('reason');

    switch (transactionType) {
      case 'purchase':
        fromAreaControl?.setValue('');
        toAreaControl?.setValue(this.pipeData?.area_code);
        reasonControl?.setValue('New stock purchase');
        break;
      case 'sale':
        fromAreaControl?.setValue(this.pipeData?.area_code);
        toAreaControl?.setValue('');
        reasonControl?.setValue('Stock sale');
        break;
      case 'transfer':
        fromAreaControl?.setValue(this.pipeData?.area_code);
        toAreaControl?.setValue('');
        reasonControl?.setValue('Stock transfer between areas');
        break;
      case 'adjustment':
        fromAreaControl?.setValue(this.pipeData?.area_code);
        toAreaControl?.setValue('');
        reasonControl?.setValue('Stock adjustment');
        break;
      case 'return':
        fromAreaControl?.setValue(this.pipeData?.area_code);
        toAreaControl?.setValue('');
        reasonControl?.setValue('Stock return to supplier');
        break;
      case 'damage':
        fromAreaControl?.setValue(this.pipeData?.area_code);
        toAreaControl?.setValue('');
        reasonControl?.setValue('Stock damaged/lost');
        break;
    }
  }

  onSaveMovement(): void {
    if (this.movementForm.invalid) {
      this.movementForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const formData = this.movementForm.value;
    formData.pipe_id = this.pipeData.pipe_id;
    formData.total_amount = formData.quantity * formData.unit_price;

    this.http.post(`${environment.apiServiceBaseUrl}/hawdco/stock-movements`, formData).subscribe({
      next: () => {
        this.loading = false;
        if (!this.isNewMovement) {
          this.loadStockMovements();
          this.movementForm.reset();
          this.onTransactionTypeChange('purchase');
        } else {
          this.dialogRef.close(true);
        }
      },
      error: (error) => {
        console.error('Error saving stock movement:', error);
        this.loading = false;
      }
    });
  }

  exportMovements(): void {
    this.http.get(`${environment.apiServiceBaseUrl}/hawdco/pipe-inventory/${this.pipeData.pipe_id}/stock-movements/export`, { 
      responseType: 'blob' 
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `stock-movements-${this.pipeData.pipe_tubo_type}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error exporting movements:', error);
      }
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  getTransactionTypeColor(type: string): string {
    switch (type) {
      case 'purchase': return 'primary';
      case 'sale': return 'accent';
      case 'transfer': return 'basic';
      case 'adjustment': return 'warn';
      case 'return': return 'warn';
      case 'damage': return 'warn';
      default: return 'basic';
    }
  }

  calculateTotalAmount(): number {
    const quantity = this.movementForm.get('quantity')?.value || 0;
    const unitPrice = this.movementForm.get('unit_price')?.value || 0;
    return quantity * unitPrice;
  }
}
